import * as React from "react"

/** Global rail hotkeys. Bail inside inputs and while any drawer/dialog is open. */

export interface ReviewHotkeyHandlers {
  onNext: () => void
  onPrev: () => void
  onConfirm: () => void
  onDismiss: () => void
  onSendToSme: () => void
  onMarkCorrected: () => void
  onEnterEdit: () => void
  onCollapse: () => void
  onFocusSearch: () => void
  onOpenShortcuts: () => void
}

export interface ReviewHotkeyOptions {
  /** True when a drawer or modal owns the keyboard — hotkeys become inert. */
  paused: boolean
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  const tag = t.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
  if (t.isContentEditable) return true
  return false
}

export function useReviewHotkeys(
  handlers: ReviewHotkeyHandlers,
  options: ReviewHotkeyOptions,
): void {
  const { paused } = options
  const ref = React.useRef(handlers)
  React.useLayoutEffect(() => {
    ref.current = handlers
  })

  React.useEffect(() => {
    if (paused) return
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const h = ref.current
      switch (e.key) {
        case "j":
        case "ArrowDown":
          e.preventDefault()
          h.onNext()
          break
        case "k":
        case "ArrowUp":
          e.preventDefault()
          h.onPrev()
          break
        case "c":
          e.preventDefault()
          h.onConfirm()
          break
        case "d":
          e.preventDefault()
          h.onDismiss()
          break
        case "s":
          e.preventDefault()
          h.onSendToSme()
          break
        case "m":
          e.preventDefault()
          h.onMarkCorrected()
          break
        case "e":
          e.preventDefault()
          h.onEnterEdit()
          break
        // Enter is intentionally NOT bound — the record header is a real
        // button; a global preventDefault would hijack every other focused
        // button/link on the page.
        case "Escape":
          h.onCollapse()
          break
        case "/":
          e.preventDefault()
          h.onFocusSearch()
          break
        case "?":
          e.preventDefault()
          h.onOpenShortcuts()
          break
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [paused])
}
